import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Calendar, ArrowRight, Search } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Link } from 'react-router-dom';
import { SEO } from '../components/SEO';

const API = process.env.REACT_APP_BACKEND_URL;

const staticPosts = [
  {
    id: 'static-1',
    title: 'Why Millets Are the Future of Indian Agriculture',
    excerpt: 'Millets are nutrient-dense, water-efficient, and climate-resilient. Here\'s why they are making a massive comeback in Indian farming and kitchens.',
    created_at: '2026-03-20T00:00:00Z',
    category: 'Agriculture',
    image: 'https://images.unsplash.com/photo-1740501813766-067394db5b64?crop=entropy&cs=srgb&fm=jpg&q=85',
    author: "Nistala's Group",
    content: ''
  },
  {
    id: 'static-2',
    title: 'Farm-to-Fork: The Journey of Your Food',
    excerpt: 'Trace the complete journey of how food travels from the fields of Indian farmers to your dining table — transparently and sustainably.',
    created_at: '2026-03-15T00:00:00Z',
    category: 'Sustainability',
    image: 'https://images.unsplash.com/photo-1663753489332-626c2786e1a0?crop=entropy&cs=srgb&fm=jpg&q=85',
    author: "Nistala's Group",
    content: ''
  },
  {
    id: 'static-3',
    title: 'Empowering Women in Agriculture',
    excerpt: 'Women make up 65% of India\'s agricultural workforce. Learn how Nistala\'s is creating equal opportunities and leadership roles for rural women.',
    created_at: '2026-03-10T00:00:00Z',
    category: 'Empowerment',
    image: 'https://images.unsplash.com/photo-1646066490017-c935b1a1eb0f?crop=entropy&cs=srgb&fm=jpg&q=85',
    author: "Nistala's Group",
    content: ''
  },
  {
    id: 'static-4',
    title: 'AI in Agriculture: Precision Farming for Small Holders',
    excerpt: 'How AI-powered tools are helping small-scale Indian farmers optimize crop yields, reduce waste, and increase profitability.',
    created_at: '2026-03-05T00:00:00Z',
    category: 'Technology',
    image: 'https://images.unsplash.com/photo-1761839257144-297ce252742e?crop=entropy&cs=srgb&fm=jpg&q=85',
    author: "Nistala's Group",
    content: ''
  },
  {
    id: 'static-5',
    title: 'The Rise of Organic Food in India',
    excerpt: 'Consumer demand for chemical-free, organic food is surging. Here\'s what\'s driving the organic revolution and how you can be part of it.',
    created_at: '2026-02-28T00:00:00Z',
    category: 'Organic',
    image: 'https://images.unsplash.com/photo-1728910156510-77488f19b152?crop=entropy&cs=srgb&fm=jpg&q=85',
    author: "Nistala's Group",
    content: ''
  },
  {
    id: 'static-6',
    title: 'Cold Chain Logistics: Keeping Your Food Fresh',
    excerpt: 'Discover how modern cold-chain technology ensures that fresh produce reaches your doorstep without losing its nutritional value.',
    created_at: '2026-02-20T00:00:00Z',
    category: 'Logistics',
    image: 'https://images.unsplash.com/photo-1761574319274-8a3a9cd80a6e?crop=entropy&cs=srgb&fm=jpg&q=85',
    author: "Nistala's Group",
    content: ''
  },
];

const formatDate = (dateStr) => {
  try {
    return new Date(dateStr).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  } catch { return dateStr; }
};

const Blogs = () => {
  const [posts, setPosts] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');

  useEffect(() => {
    fetch(`${API}/api/blogs`)
      .then(r => r.json())
      .then(data => {
        if (Array.isArray(data) && data.length > 0) setPosts(data);
        else setPosts(staticPosts);
      })
      .catch(() => setPosts(staticPosts));
  }, []);

  const categories = ['All', ...new Set(posts.map(p => p.category).filter(Boolean))];

  const filtered = posts.filter(p => {
    const matchSearch = !searchQuery || p.title.toLowerCase().includes(searchQuery.toLowerCase()) || p.excerpt.toLowerCase().includes(searchQuery.toLowerCase());
    const matchCategory = selectedCategory === 'All' || p.category === selectedCategory;
    return matchSearch && matchCategory;
  });

  return (
    <div className="min-h-screen bg-[#FDF8F0]" data-testid="blogs-page">
      <SEO title="Blog" description="Stories from the field, insights on sustainable living, and updates from our farm-to-fork ecosystem." />

      {/* Hero */}
      <section className="relative py-20 overflow-hidden">
        <div className="absolute inset-0 bg-[#2C1810]" />
        <div className="relative max-w-5xl mx-auto px-4 text-center pt-16 pb-4">
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }} className="text-xs tracking-[0.25em] uppercase text-[#D4A574] mb-4">Insights & Stories</motion.p>
          <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }} className="text-4xl sm:text-5xl font-bold text-white mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
            Our <span className="text-[#D4A574]">Blog</span>
          </motion.h1>
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.45 }} className="text-base text-white/70 max-w-xl mx-auto">
            Stories from the field, insights on sustainable living, and updates from our ecosystem.
          </motion.p>
        </div>
      </section>

      {/* Filters */}
      <section className="py-6 border-b border-[#E8DFD3]">
        <div className="max-w-6xl mx-auto px-4">
          <div className="flex flex-col sm:flex-row gap-4 items-center justify-between">
            <div className="flex gap-2 flex-wrap">
              {categories.map(cat => (
                <button
                  key={cat}
                  data-testid={`blog-filter-${cat.toLowerCase().replace(/\s/g, '-')}`}
                  onClick={() => setSelectedCategory(cat)}
                  className={`px-4 py-1.5 rounded-full text-sm font-medium transition-all cursor-pointer ${
                    selectedCategory === cat
                      ? 'bg-[#5C7A3A] text-white'
                      : 'bg-white text-[#7a6555] border border-[#E8DFD3] hover:border-[#5C7A3A] hover:text-[#5C7A3A]'
                  }`}
                >
                  {cat}
                </button>
              ))}
            </div>
            <div className="relative w-full sm:w-64">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#7a6555]" />
              <input
                data-testid="blog-search-input"
                type="text"
                placeholder="Search articles..."
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 rounded-lg border border-[#E8DFD3] bg-white text-sm text-[#2C1810] focus:outline-none focus:ring-2 focus:ring-[#5C7A3A]/20 focus:border-[#5C7A3A]"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Blog Grid */}
      <section className="py-12">
        <div className="max-w-6xl mx-auto px-4">
          {filtered.length === 0 ? (
            <div className="text-center py-16" data-testid="no-blogs-found">
              <p className="text-lg text-[#7a6555]">No articles found matching your search.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filtered.map((post, idx) => (
                <motion.div
                  key={post.id}
                  initial={{ opacity: 0, y: 25 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.08, duration: 0.5 }}
                >
                  <Link to={post.content ? `/blogs/${post.id}` : '#'} className={`block h-full ${!post.content ? 'pointer-events-none' : ''}`}>
                    <Card className="group h-full bg-white border border-[#E8DFD3] shadow-sm hover:shadow-xl hover:shadow-[#D4A574]/10 transition-all duration-500 hover:-translate-y-1 overflow-hidden cursor-pointer" data-testid={`blog-card-${post.id}`}>
                      <div className="relative h-48 overflow-hidden">
                        <img src={post.image} alt={post.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700 ease-out" />
                        <div className="absolute top-3 left-3">
                          <span className="bg-[#5C7A3A] text-white text-xs font-semibold px-3 py-1 rounded-full">{post.category}</span>
                        </div>
                      </div>
                      <CardContent className="p-5">
                        <div className="flex items-center gap-1.5 text-xs text-[#7a6555] mb-2">
                          <Calendar size={12} />
                          {formatDate(post.created_at)}
                        </div>
                        <h3 className="text-lg font-bold text-[#2C1810] mb-2 group-hover:text-[#5C7A3A] transition-colors line-clamp-2" style={{ fontFamily: 'Playfair Display, serif' }}>{post.title}</h3>
                        <p className="text-sm text-[#7a6555] leading-relaxed mb-3 line-clamp-3">{post.excerpt}</p>
                        {post.content ? (
                          <span className="inline-flex items-center text-sm font-semibold text-[#5C7A3A] group-hover:gap-2 transition-all duration-300">
                            Read More <ArrowRight size={14} className="ml-1 group-hover:translate-x-1 transition-transform duration-300" />
                          </span>
                        ) : (
                          <span className="text-xs text-[#D4A574] font-medium">Coming Soon</span>
                        )}
                      </CardContent>
                    </Card>
                  </Link>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default Blogs;
